import { getFollowPreferences } from '@gonfalon/openapi/operations/getFollowPreferences';
import { getProfileContext } from '@gonfalon/openapi/operations/getProfileContext';
import { createQueryKeys } from '@lukemorales/query-key-factory';

import { createQueryOptions } from '../internal/createQueryOptions';
import { reactQueryResponseAdapter } from '../internal/reactQueryResponseAdapter';

export const getFollowPreferencesQuery = createQueryOptions(getFollowPreferences);

/**
 * @deprecated Use `createQueryOptions` with the OpenAPI operation directly instead.
 * See packages/rest-api/README.md for the recommended pattern.
 */
export const profileContext = createQueryKeys('profileContext', {
  detail: {
    queryKey: null,
    queryFn: async ({ signal }) => reactQueryResponseAdapter(getProfileContext({}, { signal })),
  },
});

import { getSelectedView } from '@gonfalon/openapi/operations/getSelectedView';
import { putSelectedView } from '@gonfalon/openapi/operations/putSelectedView';
import { getQueryClient } from '@gonfalon/react-query-client/getQueryClient';

import { createMutationHook } from '../internal/createMutationHook';

export const getProfileContextQuery = createQueryOptions(getProfileContext);
export const getSelectedViewQuery = createQueryOptions(getSelectedView);

interface OptimisticUpdateContext {
  previousData: unknown;
  queryKey: readonly unknown[];
}

type SelectedViewPreferences = {
  selectedViewKey?: string;
  favorites?: string[];
};

export const usePutSelectedView = createMutationHook(putSelectedView, {
  onMutate: async (variables): Promise<OptimisticUpdateContext> => {
    const queryClient = getQueryClient();
    const queryKey = getSelectedViewQuery({ projKey: variables.projKey }).queryKey;

    // Cancel any outgoing refetches so they don't overwrite our optimistic update
    await queryClient.cancelQueries({ queryKey });

    // Snapshot the previous value for rollback
    const previousData = queryClient.getQueryData(queryKey);

    // Optimistically update the cache
    queryClient.setQueryData(
      queryKey,
      (previous: SelectedViewPreferences | undefined): SelectedViewPreferences => ({
        ...previous,
        selectedViewKey: variables.body.selectedViewKey,
        ...(variables.body.favorites !== undefined ? { favorites: variables.body.favorites } : {}),
      }),
    );

    // Return context with the previous data for rollback
    return { previousData, queryKey };
  },
  onError: (err, variables, context?: OptimisticUpdateContext) => {
    const queryClient = getQueryClient();
    // Rollback to the previous value on error
    if (context?.queryKey) {
      queryClient.setQueryData(context.queryKey, context.previousData);
    }
  },
  onSettled: async (data, error, variables) => {
    const queryClient = getQueryClient();
    // Always refetch to ensure we have the latest data
    await queryClient.invalidateQueries({
      queryKey: getSelectedViewQuery({ projKey: variables.projKey }).queryKey,
    });
  },
});
